import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { format } from 'date-fns';
import {
  Bell, Check, X, Clock, CheckSquare, FileText, Target, Activity,
  AlertTriangle, BookOpen, Rocket, ExternalLink, RefreshCw, User, Calendar
} from 'lucide-react';

const NOTIFICATION_ICONS = {
  task_pending_approval: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Task Approval' },
  task_tentative: { icon: CheckSquare, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Tentative Task' },
  task_rejected: { icon: X, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Task Rejected' },
  task_approved: { icon: Check, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Task Approved' },
  artifact_pending_approval: { icon: FileText, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Artifact Approval' },
  artifact_approved: { icon: FileText, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Artifact Approved' },
  artifact_rejected: { icon: FileText, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Artifact Rejected' },
  mission_status_change: { icon: Target, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Mission Update' },
  objective_activation_requested: { icon: Target, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Objective Activation' },
  objective_activated: { icon: Target, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Objective Activated' },
  activity_status_change: { icon: Activity, color: 'text-cyan-400', bg: 'bg-cyan-500/10', label: 'Activity Update' },
  issue_created: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'New Issue' },
  issue_assigned: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Issue Assigned' },
  progress_report_submitted: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Progress Report' },
  principles_validation_needed: { icon: BookOpen, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Validation Needed' },
  training_request_status: { icon: BookOpen, color: 'text-indigo-400', bg: 'bg-indigo-500/10', label: 'Training Request' },
  app_update: { icon: Rocket, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'App Update' },
  feedback_status_change: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Feedback Update' },
  feedback_needs_info: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Info Requested' }
};

export default function NotificationDetailDialog({ notification, open, onOpenChange, deepLink }) {
  if (!notification) return null;

  const iconConfig = NOTIFICATION_ICONS[notification.type] || { icon: Bell, color: 'text-slate-400', bg: 'bg-slate-500/10', label: 'Notification' };
  const Icon = iconConfig.icon;
  const metadata = notification.metadata || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notif-notificationdetaildialog-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${iconConfig.bg}`}>
              <Icon className={`w-5 h-5 ${iconConfig.color}`} />
            </div>
            <div>
              <DialogTitle className="text-slate-100">{notification.title}</DialogTitle>
              <Badge variant="outline" className={`mt-1 text-xs ${iconConfig.color} border-current/30`}>
                {iconConfig.label}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Message */}
          <div className="p-3 bg-slate-800/50 rounded-lg text-sm text-slate-300 leading-relaxed">
            {notification.message}
          </div>

          {/* Info request message */}
          {metadata.info_request_message && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-xs font-medium text-amber-400 mb-1">Message from Reviewer</p>
              <p className="text-sm text-amber-200 whitespace-pre-wrap">{metadata.info_request_message}</p>
            </div>
          )}

          {/* Rejection reason */}
          {metadata.rejection_reason && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-xs font-medium text-red-400 mb-1">Rejection Reason</p>
              <p className="text-sm text-red-300">{metadata.rejection_reason}</p>
            </div>
          )}

          {/* Admin notes for feedback */}
          {metadata.admin_notes && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs font-medium text-blue-400 mb-1">Admin Notes</p>
              <p className="text-sm text-blue-300 whitespace-pre-wrap">{metadata.admin_notes}</p>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {notification.source_code && (
              <div className="p-2.5 bg-slate-800/30 rounded-lg">
                <p className="text-xs text-slate-500 mb-0.5">Code</p>
                <p className="text-slate-300 font-mono text-xs">{notification.source_code}</p>
              </div>
            )}
            {notification.source_name && (
              <div className="p-2.5 bg-slate-800/30 rounded-lg col-span-2">
                <p className="text-xs text-slate-500 mb-0.5">Source</p>
                <p className="text-slate-300">{notification.source_name}</p>
              </div>
            )}
            {notification.triggered_by_email && (
              <div className="p-2.5 bg-slate-800/30 rounded-lg flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Triggered by</p>
                  <p className="text-slate-300 text-xs">{metadata.changed_by_name || metadata.triggered_by_name || notification.triggered_by_email}</p>
                </div>
              </div>
            )}
            <div className="p-2.5 bg-slate-800/30 rounded-lg flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Received</p>
                <p className="text-slate-300 text-xs">{format(new Date(notification.created_date), 'MMM d, yyyy h:mm a')}</p>
              </div>
            </div>
          </div>

          {/* Metadata - status changes */}
          {(metadata.previous_status && metadata.new_status) && (
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-slate-400 border-slate-600">{metadata.previous_status}</Badge>
              <span className="text-slate-500">→</span>
              <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30">{metadata.new_status}</Badge>
            </div>
          )}

          {/* Channels sent */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {notification.email_sent && <Badge variant="outline" className="text-xs text-slate-500 border-slate-700">Email sent</Badge>}
            {notification.google_chat_sent && <Badge variant="outline" className="text-xs text-slate-500 border-slate-700">Chat sent</Badge>}
            {notification.read && notification.read_at && (
              <span>Read {format(new Date(notification.read_at), 'MMM d, h:mm a')}</span>
            )}
          </div>

          {/* Action button */}
          {notification.action_url === 'refresh' ? (
            <Button
              onClick={() => { onOpenChange(false); window.location.reload(); }}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh App
            </Button>
          ) : deepLink ? (
            <Link to={createPageUrl(deepLink)} onClick={() => onOpenChange(false)}>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                <ExternalLink className="w-4 h-4 mr-2" />
                Go to Source
              </Button>
            </Link>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}